import {
  InventoryStatus,
  MecVariant,
} from '@adapters/bigcommerce/types/product';
import { buildSPUHoursMessaging } from '@utils/store/buildSPUHoursMessaging';
import {
  FEATURE_FLAG_DISABLE_SPU,
  FEATURE_FLAG_NEXT_DAY,
  FEATURE_FLAG_NO_PICKUP_TIMES,
} from '@config/feature-flag';
import { VariantName } from '@utils/optimizeAbTest/types';
import { useStoresUI } from '@context/StoresContext';
import { useTranslation } from 'next-i18next';
import { usePdp } from '@context/PdpContext';

export function useStoreAvailability(
  storeId: string,
  pickupMessage: string | null,
  abVariant: string | null,
  isModal: boolean
) {
  const { favouriteStoreDetails } = useStoresUI();
  const { t } = useTranslation('pdp');
  const { selectedAvailability, selectedVariant, quantity } = usePdp();

  const storeQuantity = selectedVariant?.storeInventory[storeId]?.quantity;
  const minPurchaseQuantityMet =
    typeof storeQuantity === 'number' &&
    typeof quantity === 'number' &&
    storeQuantity >= quantity;

  let stockIcon;
  let stockMessage;
  let availabilityMessage;

  const hasSelectedVariant = !!selectedVariant;
  if (!hasSelectedVariant) return null;

  const storeInStock =
    !!selectedVariant?.storeInventory[storeId]?.status &&
    !(
      selectedVariant?.storeInventory[storeId]?.status ===
        InventoryStatus.OUT_OF_STOCK ||
      selectedVariant?.storeInventory[storeId]?.status ===
        InventoryStatus.NOT_STOCKED
    ) &&
    minPurchaseQuantityMet;

  const storeLowStock =
    selectedVariant?.storeInventory[storeId]?.status ===
    InventoryStatus.LOW_STOCK;

  const storeOutOfStock =
    selectedVariant?.storeInventory[storeId]?.status ===
    InventoryStatus.OUT_OF_STOCK;

  const stsDCInStock =
    !!selectedVariant?.storeInventory[99]?.status &&
    !(
      selectedVariant?.storeInventory[99]?.status ===
        InventoryStatus.OUT_OF_STOCK ||
      selectedVariant?.storeInventory[99]?.status ===
        InventoryStatus.NOT_STOCKED
    );

  const stsShopInStock = !!selectedVariant?.hasStockAvailable;
  // Restricted items never ship, not even to a store: pickup needs stock there.
  const stsInStock =
    !selectedVariant?.fulfillmentRestriction &&
    (stsDCInStock || stsShopInStock);

  if (!pickupMessage && favouriteStoreDetails) {
    const spuMessageObj = buildSPUHoursMessaging(favouriteStoreDetails, t);
    pickupMessage = t(`pdp:product.pickup.short.${spuMessageObj.key}`, {
      day: spuMessageObj.day,
    });
  }

  if (selectedAvailability) {
    // Store Specific Messaging
    if (storeInStock && !storeLowStock) {
      stockMessage = t('product.in.stock');
    } else if (storeInStock && storeLowStock) {
      stockMessage = t('product.low.stock');
    } else if (!storeInStock && stsInStock && isModal) {
      stockMessage = t('product.sts.stocked');
    } else if (storeOutOfStock) {
      stockMessage = t('product.out.of.stock');
    } else {
      stockMessage = t('product.not.stocked');
    }
  }

  if (process.env.NEXT_PUBLIC_ENABLE_LOGGING === 'true') {
    console.log({
      storeInStock,
      stsInStock,
      lookingFor: !storeInStock && stsInStock,
    });
  }
  // SPU / STS Messaging
  if (
    !selectedAvailability ||
    FEATURE_FLAG_DISABLE_SPU ||
    FEATURE_FLAG_NO_PICKUP_TIMES ||
    !selectedVariant?.quickPickup
  ) {
    // Store Specific Messaging For store only products
    if (storeInStock && !storeLowStock) {
      stockIcon = 'success';
      stockMessage = t('product.in.stock.pickup');
      availabilityMessage = t('product.in.stock');
    } else if (storeInStock && storeLowStock) {
      stockIcon = 'lowstock';
      stockMessage = t('product.low.stock');
      availabilityMessage = t('product.low.stock');
    } else if (storeOutOfStock) {
      stockIcon = 'error';
      stockMessage = t('product.out.of.stock');
      availabilityMessage = t('product.out.of.stock');
    } else {
      stockIcon = 'error';
      stockMessage = t('product.not.stocked');
      availabilityMessage = t('product.not.stocked');
    }
  } else if (storeInStock) {
    stockIcon = 'success';
    availabilityMessage = FEATURE_FLAG_NEXT_DAY
      ? t('product.free.store.pickup.nextDay')
      : pickupMessage;
  } else if (!storeInStock && stsInStock && !isModal) {
    stockIcon = abVariant === VariantName.variant_1 ? 'success' : 'lowstock';
    availabilityMessage = `${t('product.pickup.short.shipToStore')}`;
  } else if (!storeInStock && stsInStock && isModal) {
    stockIcon = abVariant === VariantName.variant_1 ? 'success' : 'lowstock';
    availabilityMessage = t('product.pickup.short.shipToStore.modal');
  } else if (!storeInStock && !stsInStock) {
    stockIcon = 'error';
    availabilityMessage = t('product.pickup.unavailable');
  }

  return {
    stockIcon,
    stockMessage,
    availabilityMessage,
  };
}
